import { Preview } from "@/app/Preview"
import { observer } from "mobx-react-lite"
import { Header } from "@/app/Header"
import { SettingsSideBar } from "@/app/settings/SettingsSideBar"
import { SaveButton } from "../history/SaveButton"
import { PublishButton } from "../history/PublishButton"
import { useThema } from "@/stores/Thema"
import { keepPreviousData, useQuery } from "@tanstack/react-query"
import { useUser } from "@/stores/Auth"
import { useState } from "react"
import { useRootStore } from "@/contexts"
import { Button } from "@/components/Button"
import { SearchField } from "@/components/SearchField/SearchField"
import { Text } from "react-aria-components"
import { ThemeGroupDto, ThemeVersionDto } from "@/client"
import { EditThemeButton } from "./components/EditThemeButton"
import { CreateThemeButton } from "./components/CreateThemeButton"
import { DeleteThemeButton } from "./components/DeleteThemeButton"


export const Themer = observer(() => {
    return <>
        <SettingsSideBar/>
        <div className='flex flex-col flex-grow h-full min-h-0 min-w-0'>
            <Header/>
            <Preview/>
        </div>
        <div className="flex w-[400px] bg-slate-800 text-slate-400 h-full border border-l-slate-500">
            <ThemeVersionList/>
        </div>
    </>
})

const ThemeVersionList = observer(() => {
    const root = useRootStore()
    const thema = useThema()
    const user = useUser()
    const [search, setSearch] = useState<string>()
    const [isPublished, _setIsPublished] = useState(true)
    const [isDraft, _setIsDraft] = useState(true)
    const [offset, setOffset] = useState(0)

    const {data} = useQuery({
        queryKey: ["history", user?.selectedSite.id, {search, offset, isDraft, isPublished}],
        queryFn: async() => {
            return await root.designer.themeList({search: search})
        },
        placeholderData: keepPreviousData
    })

    return (
        <div className="w-full flex flex-col gap-4 px-4 scroller relative bg-inherit">
            <div className="text-xl bg-inherit sticky top-0 py-4 flex flex-col gap-4">
                <span><small>Loaded Theme:</small> <strong>{thema.themeName }</strong></span>
                <SearchField className="dark" onChange={setSearch} onClear={() => setSearch("")}/>
                <CreateThemeButton/>
            </div>
            <div className="flex flex-col gap-4">
                {data?.map(g => <>
                    <div className="flex flex-col gap-2 bg-slate-900 rounded-md p-4">
                        <div className="flex justify-between">
                            <div className="flex">
                                <div className="text-lg mr-1"><strong>{displayName(g)}</strong></div>
                                <EditThemeButton id={g.id} defaultValue={g.name} />
                            </div>
                            {!g.versions.find(v => v.status == "Published") && <DeleteThemeButton id={g.id} name={g.name}/>}
                        </div>
                        {g.versions.find(v => v.status == "Published") && <div className="text-sm"><strong>Published</strong></div>}
                        <div className="text-sm">Saved: <strong>{g.createdAt.toLocaleString()}</strong></div>
                        <div className="flex gap-2 pt-2 justify-start">
                            {!!g.versions.length &&
                                <Button treatment="outline" size="sm" onPress={() => thema.restore(g.id)}>
                                    <Text>LOAD</Text>
                                </Button>
                            }
                            <SaveButton themeId={g.id} />
                            {!!g.versions.length && <PublishButton groupId={g.id} />}
                        </div>
                        {g.versions.map(v => <>
                            <div className="flex flex-col gap-2 bg-slate-800 rounded-md p-4">
                                <div className="flex justify-between">
                                    <div className="flex">
                                        <div className="text-md"><strong>{displayName(v)}</strong></div>
                                        <EditThemeButton id={g.id} versionId={v.id} defaultValue={v.name} />
                                    </div>
                                    {v.status != "Published" && <DeleteThemeButton id={g.id} versionId={v.id} name={`${displayName(v)} ver ${displayName(v)}`} />}
                                </div>
                                {v.status == "Published" && <div className="text-sm">Status: <strong>{v.status}</strong></div>}
                                {v.status == "Published" && <div className="text-sm">Published: <strong>{v.publishedAt.toLocaleString()}</strong></div>}
                                <div className="text-sm">Saved: <strong>{v.createdAt.toLocaleString()}</strong></div>
                                <div className="flex gap-2 pt-2">
                                    <Button treatment="outline" size="sm" onPress={() => thema.restore(g.id, v.id)}>LOAD</Button>
                                    <PublishButton groupId={g.id} id={v.id}/>

                                </div>
                            </div>
                        </>)}
                    </div>
                </>)}
            </div>
        </div>
    )
})

function displayName(item: ThemeGroupDto | ThemeVersionDto) {
    return item.name || `#${item.id.slice(0,4)}`
}